import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { searchAciTariffs, type AciTariff } from './api';
import { formatRate } from './calc';

type Found =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly items: readonly AciTariff[] };

/**
 * The ACI vehicle search (Gestione): the make or model, the checked rows of this year, one tap to
 * take a row's reference. The server answers up to 500 rows; the first 12 are shown.
 */
export function AciSearch({ onPick }: { readonly onPick: (tariff: AciTariff) => void }) {
  const { t } = useTranslation('expenses');
  const id = useId();
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Found>({ status: 'idle' });

  const search = async (): Promise<void> => {
    if (q.trim() === '') return;
    setFound({ status: 'loading' });
    const outcome = await searchAciTariffs(new Date().getFullYear(), q.trim());
    setFound(outcome.ok ? { status: 'ready', items: outcome.value } : { status: 'failed' });
  };

  return (
    <fieldset className="border-border flex flex-col gap-2 rounded-lg border p-3">
      <legend className="px-1 text-sm font-semibold">{t('mileage.aci.title')}</legend>
      <div className="flex gap-2">
        <Input
          id={id}
          aria-label={t('mileage.aci.query')}
          value={q}
          placeholder={t('mileage.aci.placeholder')}
          onChange={(event) => setQ(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void search();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={() => void search()}>
          {t('mileage.aci.search')}
        </Button>
      </div>
      <AciResults found={found} onPick={onPick} />
    </fieldset>
  );
}

function AciResults({
  found,
  onPick,
}: {
  readonly found: Found;
  readonly onPick: (tariff: AciTariff) => void;
}) {
  const { t } = useTranslation('expenses');
  if (found.status === 'idle') return null;
  if (found.status === 'loading') return <p role="status">{t('mileage.aci.loading')}</p>;
  if (found.status === 'failed') return <p role="alert">{t('mileage.aci.failed')}</p>;
  if (found.items.length === 0) return <p role="status">{t('mileage.aci.none')}</p>;
  return (
    <ul className="flex flex-col gap-1">
      {found.items.slice(0, 12).map((tariff) => (
        <li key={tariff.id}>
          <button
            type="button"
            className="border-border hover:bg-muted flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm"
            onClick={() => onPick(tariff)}
          >
            <span className="min-w-0 truncate">
              {tariff.make} {tariff.model}
              <small className="text-muted-foreground"> · {tariff.fuel}</small>
            </span>
            <span className="text-muted-foreground shrink-0 tabular-nums">
              {formatRate(tariff.eurPerKm)} {t('mileage.rate.unit')}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
